import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';

import theme from 'theme';
import moment from 'moment';
import { Table } from 'components/UI';
import Status from 'components/Status';
import { useSelector } from 'react-redux';
import { useStyles } from './Actions.styled';
import { useHistory } from 'react-router-dom';
import { SearchField } from 'components/FormFields';
import { SourcesSelect } from 'components/DataFields';
import { getSourceLink, getDynamicRoute } from 'utils';
import { Typography, Box, Grid } from '@material-ui/core';

const ActionsTable = ({
  list,
  search,
  params,
  loading,
  handleSort,
  pagination,
  handlePageChange,
  handlePerRowsChange,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { localeFormat } = useSelector((state) => state.organizations);
  const DatetimeField = ({ datetime, tz = moment.tz.guess() }) => {
    return (
      <span>
        <p>{moment(datetime).tz(tz).format(`${localeFormat} hh:mm A`)}</p>
        <p className={classes.timezone}>{moment().tz(tz).format('UTC Z')}</p>
      </span>
    );
  };

  const [columns] = useState([
    {
      name: i18n.t('dashboard.actions.source_id'),
      selector: 'source_id',
      cell: (row) => getSourceLink(row.source_type, row.source_id, classes.link),
      style: {
        color: theme.palette.primary.main,
      },
    },
    {
      name: i18n.t('dashboard.actions.qualification'),
      selector: 'qualification',
      cell: (row) => (
        <Box display="flex" flexDirection="column" onClick={() => handleRowClick(row)}>
          <Typography component="div">
            <Box fontSize={13}>{row?.qualification?.label}</Box>
          </Typography>
          <Status status={row.qualification.status} statusKey="qualifications_statuses" />
        </Box>
      ),
    },
    {
      name: i18n.t('dashboard.actions.requirements'),
      selector: 'requirements',
      cell: (row) => row?.label || '--',
    },
    {
      name: i18n.t('common.actions'),
      selector: 'actions',
      cell: (row) => {
        if (row?.details.length > 0) {
          return row?.details.map((status, i) => {
            const last = i + 1 === row?.details.length;
            return (
              <div key={i} onClick={() => handleRowClick(row)}>
                <Typography component="span">
                  <Box fontSize={13} className={classes[status]}>
                    {`${i18n.t(`qualsActions.${status}`, {
                      defaultValue: status,
                    })}${!last ? ', ' : ''}`}
                  </Box>
                </Typography>
                {!last ? ', ' : ''}
              </div>
            );
          });
        }
        return '--';
      },
    },
    {
      name: i18n.t('dashboard.actions.started_time'),
      selector: 'started_time',
      cell: (row) =>
        row.started_time && moment(row.started_time).isValid() ? (
          <DatetimeField datetime={row?.started_time} />
        ) : (
          '--'
        ),
      sortable: true,
    },
    {
      name: i18n.t('dashboard.actions.modify_time'),
      selector: 'modified_time',
      cell: (row) =>
        row.modified_time && moment(row.modified_time).isValid() ? (
          <DatetimeField datetime={row?.modified_time} />
        ) : (
          '--'
        ),
      sortable: true,
    },
  ]);

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(getDynamicRoute(row?.source_type, row?.source_id));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box className={classes.subheader}>
          <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="flex-end">
            <Grid item xs={12} sm={4} md={3}>
              <SearchField
                allowClear
                margin="none"
                label={i18n.t('dashboard.actions.source_id')}
                field={{
                  onChange: search,
                  name: 'source_id',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <SourcesSelect
                margin="none"
                datacy="sources-filter"
                form={{
                  setFieldValue: search,
                }}
                field={{
                  name: 'source_type',
                  value: params.source_type || '',
                }}
                label={i18n.t('dashboard.actions.source')}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Table
          data={list}
          columns={columns}
          loading={loading}
          onSort={handleSort}
          handleRowClicked={handleRowClick}
          handlePageChange={handlePageChange}
          totalRows={pagination?.totalRows || 0}
          handlePerRowsChange={handlePerRowsChange}
        />
      </Grid>
    </Grid>
  );
};

ActionsTable.propTypes = {
  list: PropTypes.array.isRequired,
  search: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  pagination: PropTypes.object.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handlePerRowsChange: PropTypes.func.isRequired,
};

ActionsTable.defaultProps = {
  list: [],
  pagination: {},
  loading: false,
  search: () => {},
  handlePageChange: () => {},
  handlePerRowsChange: () => {},
};

export default ActionsTable;
